// /src/pages/NewsPage.jsx
import React from "react";
import { useLocation } from "react-router-dom";
import { getLangFromPath } from "../i18n/lang";
import SubscribeNewsModal from "../components/SubscribeNewsModal";
import { useTranslation } from "react-i18next";

import NewsUk from "./static/NewsPage.uk.jsx";
import NewsEn from "./static/NewsPage.en.jsx";
import NewsIt from "./static/NewsPage.it.jsx";
import NewsFr from "./static/NewsPage.fr.jsx";

const API = process.env.REACT_APP_API_URL || "https://api.radiomonitor.net/api";

export default function NewsPage() {
  const loc = useLocation();
  const lang = getLangFromPath(loc.pathname);
  const Map = { uk: NewsUk, en: NewsEn, it: NewsIt, fr: NewsFr };
  const L = Map[lang] || NewsUk;
  const { t } = useTranslation("common");
  const errorTexts = React.useMemo(
    () => ({
      uk: "Спробуйте ще раз пізніше.",
      en: "Please try again later.",
      it: "Riprova più tardi.",
      fr: "Veuillez réessayer plus tard.",
    }),
    []
  );

  const [subscribeOpen, setSubscribeOpen] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [pendingEmailCheck, setPendingEmailCheck] = React.useState(false);
  const [rateUntilTs, setRateUntilTs] = React.useState(0); // unix ts из X-RateLimit-Reset/Retry-After

  const handleSubmit = React.useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${API}/news/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, locale: lang || "uk", _hp: "" }),
      });

      const rlReset = Number(res.headers.get("X-RateLimit-Reset") || 0);
      const retryAfter = Number(res.headers.get("Retry-After") || 0);
      if (res.status === 429) {
        const until = retryAfter ? Math.floor(Date.now()/1000) + retryAfter : rlReset;
        if (until) setRateUntilTs(until);
        setPendingEmailCheck(true);
        setSubscribeOpen(false);
        setErrorMessage(
          (lang === "uk" && `Забагато запитів. Спробуйте через ${retryAfter || "60"} с.`) ||
          (lang === "it" && `Troppe richieste. Riprova tra ${retryAfter || "60"} s.`) ||
          (lang === "fr" && `Trop de requêtes. Réessayez dans ${retryAfter || "60"} s.`) ||
          `Too many requests. Retry in ${retryAfter || "60"} s.`
        );
        return;
      }

      if (!res.ok) {
        let detail = "";
        try { detail = (await res.json())?.detail || ""; } catch {}
        setErrorMessage(detail || errorTexts[lang] || errorTexts.uk);
        return;
      }

      const data = await res.json();
      if (data?.ok) {
        setSubscribeOpen(false);
        setErrorMessage("");
        setPendingEmailCheck(true);
        const until = rlReset || (retryAfter ? Math.floor(Date.now()/1000) + retryAfter : 0);
        if (until) setRateUntilTs(until);
      } else {
        setErrorMessage(errorTexts[lang] || errorTexts.uk);
      }
    } catch (err) {
      console.error("subscribe error", err);
      setErrorMessage(errorTexts[lang] || errorTexts.uk);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, email, lang, errorTexts]);

  const handleResend = React.useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    setErrorMessage("");

    try {
      const res = await fetch(`${API}/news/request-confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, locale: lang || "uk", _hp: "" }),
      });

      const rlReset = Number(res.headers.get("X-RateLimit-Reset") || 0);
      const retryAfter = Number(res.headers.get("Retry-After") || 0);
      if (res.status === 429) {
        const until = retryAfter ? Math.floor(Date.now()/1000) + retryAfter : rlReset;
        if (until) setRateUntilTs(until);
        setErrorMessage(
          (lang === "uk" && `Забагато запитів. Спробуйте через ${retryAfter || "60"} с.`) ||
          (lang === "it" && `Troppe richieste. Riprova tra ${retryAfter || "60"} s.`) ||
          (lang === "fr" && `Trop de requêtes. Réessayez dans ${retryAfter || "60"} s.`) ||
          `Too many requests. Retry in ${retryAfter || "60"} s.`
        );
        return;
      }

      if (!res.ok) {
        let detail = "";
        try { detail = (await res.json())?.detail || ""; } catch {}
        setErrorMessage(detail || errorTexts[lang] || errorTexts.uk);
        return;
      }

      const until = rlReset || (retryAfter ? Math.floor(Date.now()/1000) + retryAfter : 0);
      if (until) setRateUntilTs(until);
    } catch (err) {
      console.error("resend error", err);
      setErrorMessage(errorTexts[lang] || errorTexts.uk);
    } finally {
      setSubmitting(false);
    }
  }, [submitting, email, lang, errorTexts]);

  const handleClose = React.useCallback(() => {
    if (submitting) return;
    setSubscribeOpen(false);
    setErrorMessage("");
    setEmail("");
  }, [submitting]);

  return (
    <>
      <L onSubscribeClick={() => setSubscribeOpen(true)} />
      {pendingEmailCheck && (
        <div
          style={{
            margin: "16px 0 0",
            padding: "16px 18px",
            background: "#f0fff5",
            border: "1px solid #bfe7cf",
            borderRadius: 10,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            {t("modals.subscribe_news.pending_title", "Перевірте пошту")}
          </div>
          <div style={{ color: "#222", marginBottom: 12 }}>
            {t(
              "modals.subscribe_news.pending_text",
              "Ми надіслали лист із посиланням для підтвердження підписки."
            )}
          </div>

          {errorMessage && (
            <div style={{ color: "#e54848", marginBottom: 8 }}>{errorMessage}</div>
          )}

          <ResendBlock
            lang={lang}
            email={email}
            submitting={submitting}
            rateUntilTs={rateUntilTs}
            onResend={handleResend}
          />
        </div>
      )}
      <SubscribeNewsModal
        open={subscribeOpen}
        onClose={handleClose}
        email={email}
        onEmailChange={setEmail}
        submitting={submitting}
        errorMessage={errorMessage}
        onSubmit={handleSubmit}
      />
    </>
  );
}

function ResendBlock({ lang, email, submitting, rateUntilTs, onResend }) {
  const { t } = require("react-i18next").useTranslation("common");
  const [nowTs, setNowTs] = React.useState(() => Math.floor(Date.now() / 1000));
  React.useEffect(() => {
    const id = setInterval(() => setNowTs(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  const wait = Math.max(0, Number(rateUntilTs || 0) - nowTs);
  const disabled = submitting || !email.trim() || wait > 0;

  const baseLabel = t("modals.subscribe_news.resend", "Надіслати ще раз");
  const inXsec = t("modals.subscribe_news.resend_in_s", "через {{sec}} с", { sec: wait });
  const arrive = t("modals.subscribe_news.will_arrive_at", "Лист прийде на {{email}}", { email: email || "…" });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <button
        onClick={onResend}
        disabled={disabled}
        style={{
          padding: "10px 18px",
          fontSize: 16,
          borderRadius: 8,
          border: "none",
          background: "#00723a",
          color: "#fff",
          fontWeight: 700,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {wait > 0 ? `${baseLabel} (${inXsec})` : baseLabel}
      </button>
      <div style={{ color: "#666" }}>{arrive}</div>
    </div>
  );
}
